import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Inventory as InventoryIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Undo as UndoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import InventoryPlanItemReservations from '../InventoryPlanItemReservations';
import type { EvolutionPlanExecutionRecord } from './evolutionPlanExecution';
import { formatEvolutionPlanExecutionSummaryLine } from './evolutionPlanExecution';
import {
  evolutionSectionHeadingSx,
  evolutionClickableRowSxFor,
} from './evolutionTheme';

const todayLocalISODate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type PlanItem = {
  id: string;
  procedure_id?: string;
  plan_id?: string;
  procedure_name: string;
  status: string;
  quantity_required?: number;
  execution_count?: number;
  plan_title?: string;
  plan_number?: string;
  budget_number?: string;
  required_materials?: any[];
  stage_name?: string;
  stage?: string;
  clinic_procedures?: { default_stage?: string };
  executions?: any[];
  inventory_reservations?: any[];
};

type Props = {
  patientId: string;
  disabled?: boolean;
  compact?: boolean;
  evolutionId?: string | null;
  defaultProfessionalId?: string;
  executedInSession: EvolutionPlanExecutionRecord[];
  onExecuted: (record: EvolutionPlanExecutionRecord, executionId: string) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  professionalOptions: Array<{ id: string; full_name: string; specialty?: string }>;
  onNeedProfessionals?: () => void;
};

const EvolutionProcedureExecutionPanel: React.FC<Props> = ({
  patientId,
  disabled,
  compact = false,
  evolutionId,
  defaultProfessionalId,
  executedInSession,
  onExecuted,
  onError,
  onSuccess,
  professionalOptions,
  onNeedProfessionals,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approved, setApproved] = useState<PlanItem[]>([]);
  const [pending, setPending] = useState<PlanItem[]>([]);
  const [executeOpen, setExecuteOpen] = useState(false);
  const [executingItem, setExecutingItem] = useState<PlanItem | null>(null);
  const [executeSaving, setExecuteSaving] = useState(false);
  const [executeForm, setExecuteForm] = useState({
    professional_id: '',
    referring_professional_id: '',
    notes: '',
    date_mode: 'today' as 'today' | 'custom',
    executed_date: todayLocalISODate(),
  });

  const loadProcedures = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const { data: body } = await axios.get(getApiUrl(`/clinic/patients/${patientId}/procedures`));
      const payload = body?.data ?? body;
      setApproved(Array.isArray(payload?.approved) ? payload.approved : []);
      setPending(Array.isArray(payload?.pending) ? payload.pending : []);
    } catch (err: any) {
      onError?.(err?.response?.data?.error || 'Erro ao carregar procedimentos do paciente');
    } finally {
      setLoading(false);
    }
  }, [patientId, onError]);

  useEffect(() => {
    void loadProcedures();
  }, [loadProcedures]);

  const handleProcedureItemStatusChange = async (itemId: string, status: string) => {
    try {
      const { data: res } = await axios.patch(getApiUrl(`/clinic/treatment-plan-items/${itemId}/status`), { status });
      const warnings: string[] = res.data?.reservation_warnings || res.data?.data?.reservation_warnings || [];
      await loadProcedures();
      if (warnings.length > 0) {
        onSuccess?.(`Status atualizado. Avisos de reserva: ${warnings.join('; ')}`);
      }
    } catch (err: any) {
      onError?.(err.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const openExecute = (item: PlanItem) => {
    setExecutingItem(item);
    setExecuteForm({
      professional_id: defaultProfessionalId || '',
      referring_professional_id: '',
      notes: '',
      date_mode: 'today',
      executed_date: todayLocalISODate(),
    });
    onNeedProfessionals?.();
    setExecuteOpen(true);
  };

  const confirmExecute = async () => {
    if (!executingItem || !executeForm.professional_id) return;
    if (executeForm.date_mode === 'custom' && !executeForm.executed_date) {
      onError?.('Selecione a data de execução');
      return;
    }
    setExecuteSaving(true);
    try {
      const payload: Record<string, unknown> = {
        professional_id: executeForm.professional_id,
        referring_professional_id: executeForm.referring_professional_id || undefined,
        notes: executeForm.notes || undefined,
        executed_at: executeForm.date_mode === 'custom' ? executeForm.executed_date : undefined,
      };
      if (evolutionId) payload.evolution_id = evolutionId;
      const { data: res } = await axios.post(
        getApiUrl(`/clinic/treatment-plan-items/${executingItem.id}/execute`),
        payload,
      );
      const execution = res?.data?.execution || res?.execution || res?.data?.data?.execution;
      const executionId = String(execution?.id || '');
      if (!executionId) throw new Error('Execução concluída, mas o id não retornou na resposta.');

      const warnings = res.data?.stock_warnings || res.data?.data?.stock_warnings || [];
      const treatmentCrm = res.data?.treatment_crm || res.data?.data?.treatment_crm;
      let successMsg = warnings.length > 0
        ? `Executado! Alertas de estoque: ${warnings.join('; ')}`
        : 'Procedimento executado com sucesso!';
      if (treatmentCrm?.pending_items > 0) {
        successMsg += ` · ${treatmentCrm.pending_items} procedimento(s) pendente(s) — card CRM atualizado.`;
      } else if (treatmentCrm && treatmentCrm.pending_items === 0) {
        successMsg += ' · Tratamento concluído no CRM.';
      }

      const doneBefore = Number(executingItem.execution_count) || 0;
      const total = Math.max(1, Number(executingItem.quantity_required) || 1);
      const sessionNumber = Math.min(doneBefore + 1, total);
      const execProf = professionalOptions.find((p) => p.id === executeForm.professional_id);
      const refProf = professionalOptions.find((p) => p.id === executeForm.referring_professional_id);

      const record: EvolutionPlanExecutionRecord = {
        name: executingItem.procedure_name,
        plan_execution: true,
        treatment_plan_item_id: executingItem.id,
        procedure_execution_id: executionId,
        executed_at: execution?.executed_at || new Date().toISOString(),
        session_number: sessionNumber,
        sessions_total: total,
        executor_professional_name: execProf?.full_name,
        referring_professional_name: refProf?.full_name,
        notes: executeForm.notes?.trim() || undefined,
      };

      onExecuted(record, executionId);
      onSuccess?.(successMsg);
      setExecuteOpen(false);
      void loadProcedures();
    } catch (err: any) {
      onError?.(err?.response?.data?.error || 'Erro ao executar procedimento');
    } finally {
      setExecuteSaving(false);
    }
  };

  const handleUndoExecution = async (itemId: string) => {
    if (!window.confirm('Desfazer a execução deste procedimento? Os materiais consumidos serão devolvidos ao estoque.')) return;
    try {
      await axios.post(getApiUrl(`/clinic/treatment-plan-items/${itemId}/undo-execution`));
      onSuccess?.('Execução desfeita com sucesso');
      void loadProcedures();
    } catch (err: any) {
      onError?.(err?.response?.data?.error || 'Erro ao desfazer execução');
    }
  };

  const renderApprovedItem = (item: PlanItem) => {
    const total = Math.max(1, Number(item.quantity_required) || 1);
    const done = Number(item.execution_count) || 0;
    const canExecute = item.status === 'in_progress' && done < total;
    return (
      <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #3b82f6' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.procedure_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.budget_number ? `Orçamento ${item.budget_number}` : `Plano: ${item.plan_title || item.plan_number}`}
              {total > 1 ? ` · ${done}/${total} executada(s)` : ''}
            </Typography>
            {(item.executions || []).length > 0 && (
              <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {(item.executions || []).map((ex: any, idx: number) => {
                  const isLast = idx === (item.executions || []).length - 1;
                  return (
                    <Box key={ex.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
                      <Typography variant="caption" sx={{ color: '#065f46' }}>
                        Sessão {idx + 1}: {ex.executed_at ? new Date(ex.executed_at).toLocaleDateString('pt-BR') : '—'}
                        {ex.clinic_professionals?.full_name ? ` · Executor: ${ex.clinic_professionals.full_name}` : ''}
                        {ex.referring_professional?.full_name ? ` · Indicador: ${ex.referring_professional.full_name}` : ''}
                      </Typography>
                      {isLast && !disabled && (
                        <Tooltip title="Desfazer esta sessão">
                          <IconButton size="small" onClick={() => void handleUndoExecution(item.id)} sx={{ color: '#ef4444', p: 0.25 }}>
                            <UndoIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={item.status === 'cancelled' ? 'cancelled' : item.status || 'in_progress'}
                onChange={(e) => void handleProcedureItemStatusChange(item.id, e.target.value as string)}
              >
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="in_progress">Aprovado para executar</MenuItem>
                <MenuItem value="cancelled">Cancelar item</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayIcon />}
              disabled={disabled || !canExecute}
              onClick={() => openExecute(item)}
              sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', fontWeight: 600 }}
            >
              {total > 1
                ? (done >= total ? 'Concluído' : `Executar (${Math.min(done + 1, total)}/${total})`)
                : 'Executar'}
            </Button>
          </Box>
        </Box>
        <InventoryPlanItemReservations
          planItemId={item.id}
          patientId={patientId}
          treatmentPlanId={item.plan_id}
          procedureId={item.procedure_id}
          reservations={item.inventory_reservations}
          onChanged={loadProcedures}
        />
      </Paper>
    );
  };

  const renderPendingItem = (item: PlanItem) => (
    <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #f59e0b', opacity: 0.85 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.procedure_name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {item.budget_number ? `Orçamento ${item.budget_number}` : `Plano: ${item.plan_title || item.plan_number}`}
            {Number(item.quantity_required) > 1
              ? ` · ${Number(item.execution_count) || 0}/${item.quantity_required} executada(s)`
              : ''}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={item.status === 'cancelled' ? 'cancelled' : item.status || 'pending'}
            onChange={(e) => void handleProcedureItemStatusChange(item.id, e.target.value as string)}
          >
            <MenuItem value="pending">Pendente</MenuItem>
            <MenuItem value="in_progress">Aprovado para executar</MenuItem>
            <MenuItem value="cancelled">Cancelar item</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );

  if (!patientId) return null;

  const actionCount = approved.length + pending.length;
  const sessionCount = executedInSession.length;

  return (
    <Box>
      <Box
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        role="button"
        tabIndex={0}
        sx={{
          ...evolutionClickableRowSxFor(false, compact),
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ ...evolutionSectionHeadingSx, fontSize: compact ? '0.72rem' : undefined }}>
            PROCEDIMENTOS EXECUTADOS
            {sessionCount > 0 ? (
              <Typography component="span" variant="caption" sx={{ ml: 0.75, fontWeight: 600, color: '#059669' }}>
                ({sessionCount} nesta evolução)
              </Typography>
            ) : null}
          </Typography>
          {!expanded && sessionCount > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
              {executedInSession.map((rec) => (
                <Chip
                  key={rec.procedure_execution_id}
                  size="small"
                  label={formatEvolutionPlanExecutionSummaryLine(rec)}
                  sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                />
              ))}
            </Stack>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <IconButton
            size="small"
            aria-label="Atualizar procedimentos"
            onClick={(e) => {
              e.stopPropagation();
              void loadProcedures();
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={expanded ? 'Recolher' : 'Expandir'}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s' }} />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ pt: compact ? 1 : 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} sx={{ color: '#3b82f6' }} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {sessionCount > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {executedInSession.map((rec) => (
                    <Chip
                      key={rec.procedure_execution_id}
                      size="small"
                      color="success"
                      variant="outlined"
                      label={formatEvolutionPlanExecutionSummaryLine(rec)}
                      sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                    />
                  ))}
                </Stack>
              )}

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e40af' }}>
                    Liberados — aprovados para executar ({approved.length})
                  </Typography>
                </Box>
                {approved.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                    <Typography variant="body2" color="text.secondary">Nenhum liberado.</Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1}>{approved.map(renderApprovedItem)}</Stack>
                )}
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>
                    Pendentes — aguardando liberação ({pending.length})
                  </Typography>
                </Box>
                {pending.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                    <Typography variant="body2" color="text.secondary">Nenhum pendente</Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1}>{pending.map(renderPendingItem)}</Stack>
                )}
              </Box>

              {actionCount === 0 && sessionCount === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                  Nenhum procedimento pendente ou liberado para este paciente.
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Collapse>

      <Dialog open={executeOpen} onClose={() => setExecuteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Executar Procedimento
          <IconButton onClick={() => setExecuteOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {executingItem && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{executingItem.procedure_name}</Typography>
                {(executingItem.stage_name || executingItem.stage || executingItem.clinic_procedures?.default_stage) && (
                  <Chip
                    size="small"
                    label={`Etapa: ${executingItem.stage_name || executingItem.stage || executingItem.clinic_procedures?.default_stage}`}
                    sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
                  />
                )}
              </Box>
              {Number(executingItem.quantity_required) > 1 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Será registrada apenas a sessão {Math.min((Number(executingItem.execution_count) || 0) + 1, Number(executingItem.quantity_required))} de {executingItem.quantity_required}.
                  {' '}As demais continuam liberadas para executar em outro dia.
                </Alert>
              )}
              {(executingItem.required_materials?.length ?? 0) > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: '#8b5cf6' }} /> Materiais que serão consumidos
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Consumo</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Estoque</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Após</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(executingItem.required_materials ?? []).map((m: any) => {
                          const inv = m.clinic_inventory_items;
                          const needed = Number(m.quantity_per_execution || 1);
                          const current = Number(inv?.current_stock ?? 0);
                          const after = current - needed;
                          const negative = after < 0;
                          const perExecution = Math.max(Number(m.quantity_per_execution || 1), 0.001);
                          const remainingCoverage = Math.max(0, Math.floor(after / perExecution));
                          return (
                            <TableRow key={m.id}>
                              <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{inv?.name || '?'}</Typography></TableCell>
                              <TableCell align="center">{needed} {inv?.unit || ''}</TableCell>
                              <TableCell align="center">{current}</TableCell>
                              <TableCell align="center" sx={{ color: negative ? '#dc2626' : '#059669', fontWeight: 700 }}>
                                {after.toFixed(1)} {negative && <WarningIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />}
                                {!negative && (
                                  <Typography variant="caption" display="block" sx={{ color: '#047857', fontSize: '0.62rem' }}>
                                    cobre +{remainingCoverage} execução(ões)
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Profissional executor *</InputLabel>
                <Select
                  value={executeForm.professional_id}
                  label="Profissional executor *"
                  onChange={(e) => setExecuteForm({ ...executeForm, professional_id: e.target.value })}
                >
                  {professionalOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.full_name}{p.specialty ? ` — ${p.specialty}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Profissional indicador</InputLabel>
                <Select
                  value={executeForm.referring_professional_id}
                  label="Profissional indicador"
                  onChange={(e) => setExecuteForm({ ...executeForm, referring_professional_id: e.target.value })}
                >
                  <MenuItem value=""><em>Nenhum</em></MenuItem>
                  {professionalOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.full_name}{p.specialty ? ` — ${p.specialty}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
                O indicador recebe comissão de indicação executada (ex.: valor fixo por Faco/Refrativa), conforme regras cadastradas.
              </Typography>
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#334155', mb: 0.5 }}>
                  Data da execução
                </FormLabel>
                <RadioGroup
                  row
                  value={executeForm.date_mode}
                  onChange={(e) => setExecuteForm({
                    ...executeForm,
                    date_mode: e.target.value as 'today' | 'custom',
                    executed_date: e.target.value === 'today'
                      ? todayLocalISODate()
                      : executeForm.executed_date,
                  })}
                >
                  <FormControlLabel value="today" control={<Radio size="small" />} label="Executar Hoje" />
                  <FormControlLabel value="custom" control={<Radio size="small" />} label="Data Personalizada" />
                </RadioGroup>
              </FormControl>
              {executeForm.date_mode === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  required
                  type="date"
                  label="Data da execução"
                  InputLabelProps={{ shrink: true }}
                  value={executeForm.executed_date}
                  onChange={(e) => setExecuteForm({ ...executeForm, executed_date: e.target.value })}
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                fullWidth
                size="small"
                label="Observações (opcional)"
                multiline
                rows={2}
                value={executeForm.notes}
                onChange={(e) => setExecuteForm({ ...executeForm, notes: e.target.value })}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setExecuteOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              executeSaving
              || !executeForm.professional_id
              || (executeForm.date_mode === 'custom' && !executeForm.executed_date)
            }
            onClick={() => void confirmExecute()}
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', fontWeight: 600 }}
          >
            {executeSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirmar Execução'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvolutionProcedureExecutionPanel;
